import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Image,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Badge,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useBreakpointValue,
  Container,
  useToast,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Grid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  Avatar,
  Tooltip,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Icon,
} from '@chakra-ui/react';
import {
  HamburgerIcon,
  ChevronDownIcon,
  SearchIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import { FaShoppingCart, FaUser, FaBox, FaCalculator, FaTruck, FaWallet, FaSignOutAlt, FaShoppingBag, FaPrint, FaTag, FaQuestionCircle, FaPhone, FaHome, FaLock, FaMapMarkerAlt, FaInfoCircle, FaCog } from 'react-icons/fa';
import MessageModel from './MessageModelComponent';
import API from '../Api';
import { safeApiCall } from '../utils/apiErrorHandler';
import { GRADIENTS, COLORS, SPACING, TYPOGRAPHY } from '../constants/theme';
import { useSettings, usePages } from '../hooks';

const Header = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isMobileMenuOpen, onOpen: onMobileMenuOpen, onClose: onMobileMenuClose } = useDisclosure();

  // Use custom hooks for cached data
  const { settings: Setting } = useSettings();
  const { pages: pagesList } = usePages();

  // State management
  const [state, setState] = useState({
    name: '',
    categoryList: [],
    isLogin: false,
    printCash: 0,
    cartItem: [],
    SearchValue: '',
    ActiveMenu: 0,
    messageModel: false,
    message_id: 0,
    title: 'test',
    description: 'test',
    image: '',
    cartlength: 0,
    iscartProgress: false,
    isPcashProgress: false
  });

  const [selectedCategory, setSelectedCategory] = useState(0);

  // Responsive values
  const isMobile = useBreakpointValue({ base: true, lg: false });

  // API Methods
  const getMessageData = useCallback(async () => {
    try {
      const res = await API.get('sitemessage/get');
      if (res.data.data.data.length > 0) {
        const messageData = res.data.data.data[0];
        const storedMessageId = localStorage.getItem('message_id');

        if (!storedMessageId || JSON.parse(storedMessageId) !== messageData.id) {
          setState(prev => ({
            ...prev,
            title: messageData.title,
            description: messageData.description,
            message_id: messageData.id,
            image: res.data.data.url + messageData.image,
            messageModel: false
          }));
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('tocken');
        navigate('/login');
      }
    }
  }, [navigate]);

  // Removed: getSettingData and loadPagesData - now using hooks

  const checkLogin = useCallback(async () => {
    const token = localStorage.getItem('tocken');
    if (token) {
      try {
        const AuthStr = 'Bearer ' + token;
        const res = await API.get('me', { headers: { Authorization: AuthStr } });
        
        const userData = res.data?.data || res.data;
        const userName = userData?.full_name || userData?.first_name || userData?.name || userData?.phone || 'User';
        setState(prev => ({
          ...prev,
          name: userName,
          isLogin: true
        }));
        getUserCart();
        getUserPrintCash();
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem('tocken');
          setState(prev => ({ ...prev, isLogin: false }));
        }
      }
    }
  }, []);

  const getUserCart = useCallback(async () => {
    const token = localStorage.getItem('tocken');
    if (!token) return;

    setState(prev => ({ ...prev, iscartProgress: true }));
    try {
      const AuthStr = 'Bearer '.concat(token);
      const res = await API.get('users/cart/get/details', {
        headers: { Authorization: AuthStr }
      });
      setState(prev => ({
        ...prev,
        cartItem: res.data.data,
        cartlength: res.data.data.length,
        iscartProgress: false
      }));
    } catch (error) {
      setState(prev => ({ ...prev, iscartProgress: false }));
      if (error.response?.status === 401) {
        localStorage.removeItem('tocken');
        navigate('/login');
      }
    }
  }, [navigate]);

  const getUserPrintCash = useCallback(async () => {
    const token = localStorage.getItem('tocken');
    if (!token) return;

    setState(prev => ({ ...prev, isPcashProgress: true }));
    try {
      const AuthStr = 'Bearer '.concat(token);
      const res = await API.get('users/user/printcash', {
        headers: { Authorization: AuthStr }
      });
      
      setState(prev => ({
        ...prev,
        printCash: res.data.data || 0,
        isPcashProgress: false
      }));
    } catch (error) {
      setState(prev => ({ ...prev, isPcashProgress: false }));
      if (error.response?.status === 401) {
        localStorage.removeItem('tocken');
        navigate('/login');
      }
    }
  }, [navigate]);

  const loadCategoryWithProduct = useCallback(async () => {
    const result = await safeApiCall(
      () => API.get('category/active-category-product/get'),
      [],
      'Loading categories',
      toast,
      'category/active-category-product/get'
    );

    if (result.success) {
      setState(prev => ({ ...prev, categoryList: result.data.data }));
    } else {
      setState(prev => ({ ...prev, categoryList: [] }));

      if (result.error && result.error.type === 'malformedUrl') {
      }
    }
  }, [toast]);

  // Event handlers
  const submitForm = useCallback((event) => {
    event.preventDefault();
    if (state.SearchValue.trim()) {
      navigate(`/search/${state.SearchValue}`);
    }
  }, [state.SearchValue, navigate]);

  const updateForm = useCallback((event) => {
    const value = event.target.value;
    setState(prev => ({ ...prev, SearchValue: value }));
  }, []);

  const logout = useCallback((e) => {
    e.preventDefault();
    localStorage.removeItem('tocken');
    setState(prev => ({
      ...prev,
      isLogin: false,
      name: '',
      cartItem: [],
      cartlength: 0,
      printCash: 0
    }));
    navigate('/');
  }, [navigate]);

  const updateMessageModelState = useCallback((show) => {
    setState(prev => ({ ...prev, messageModel: show }));
    if (!show) {
      localStorage.setItem('message_id', state.message_id);
    }
  }, [state.message_id]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    updateCart: getUserCart,
    updatePrintCash: getUserPrintCash
  }));

  // Effects
  useEffect(() => {
    loadCategoryWithProduct();
    checkLogin();
    getMessageData();
    // Note: Pages and Settings are now loaded via hooks automatically
  }, [loadCategoryWithProduct, checkLogin, getMessageData]);

  // Calculate cart total with memoization
  const cartTotal = useMemo(() =>
    state.cartItem.reduce((total, item) => total + parseFloat(item.printingChagres || item.total || 0), 0),
    [state.cartItem]
  );

  return (
    <Box
      as="header"
      bg="white"
      borderBottom="1px"
      borderColor="gray.200"
      position="sticky"
      top="0"
      zIndex="1000"
      boxShadow="sm"
    >
      <MessageModel
        show={state.messageModel}
        title={state.title}
        description={state.description}
        image={state.image}
        UpdateShowParent={updateMessageModelState}
      />

      {/* Full Width Container */}
      <Box maxW="100%" px={{ base: 4, md: 8, lg: 12 }}>
        {/* Main Header */}
        <Flex h="64px" alignItems="center" justifyContent="space-between">
          {/* Logo */}
          <Link to="/">
            <Image
              src="/Image/logo.png"
              alt="Printster.in"
              h="56px"
              objectFit="contain"
            />
          </Link>

          {/* Search Box */}
          {!isMobile && (
            <Box flex="1" maxW="600px" mx={8}>
              <form onSubmit={submitForm}>
                <InputGroup size="md">
                  <Input
                    placeholder="Search for products..."
                    value={state.SearchValue}
                    onChange={updateForm}
                    bg="gray.50"
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="lg"
                    fontSize="14px"
                    _hover={{ borderColor: 'gray.300', bg: 'white' }}
                    _focus={{ borderColor: '#4F47E5', boxShadow: '0 0 0 1px #4F47E5', bg: 'white' }}
                    pl={4}
                    pr={10}
                    h="44px"
                  />
                  <InputRightElement h="44px" pr={2}>
                    <IconButton
                      type="submit"
                      icon={<SearchIcon />}
                      size="sm"
                      bg="transparent"
                      color="gray.500"
                      _hover={{ color: 'gray.700' }}
                      _active={{ bg: 'transparent' }}
                      aria-label="Search"
                      border="none"
                    />
                  </InputRightElement>
                </InputGroup>
              </form>
            </Box>
          )}

          {/* Desktop Navigation - Centered */}
          {!isMobile && (
            <HStack spacing={0} alignItems="center">
              {/* Catalog - Mega Menu with Tabs */}
              <Popover trigger="hover" placement="bottom" closeOnBlur={false} gutter={8}>
                <PopoverTrigger>
                  <Button
                    rightIcon={<ChevronDownIcon />}
                    variant="ghost"
                    size="md"
                    fontWeight="600"
                    fontSize="14px"
                    color="gray.800"
                    bg="transparent"
                    border="none"
                    outline="none"
                    textDecoration="none"
                    _hover={{
                      bg: 'transparent',
                      bgGradient: GRADIENTS.primary,
                      bgClip: 'text',
                      textDecoration: 'none'
                    }}
                    _active={{
                      bg: 'transparent'
                    }}
                    _focus={{
                      boxShadow: 'none',
                      border: 'none',
                      outline: 'none',
                      bg: 'transparent'
                    }}
                    px={3}
                  >
                    All Products
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  w="900px"
                  maxH="600px"
                  boxShadow="lg"
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  bg="white"
                  _focus={{ outline: 'none' }}
                >
                  <PopoverBody p={0}>
                    <Tabs
                      variant="unstyled"
                      orientation="vertical"
                      index={selectedCategory}
                      onChange={setSelectedCategory}
                    >
                      <Flex>
                        {/* Left Sidebar - Category Tabs */}
                        <Box
                          w="220px"
                          bg="gray.50"
                          borderRight="1px"
                          borderColor="gray.200"
                          borderTopLeftRadius="md"
                          borderBottomLeftRadius="md"
                          overflowY="hidden"
                          maxH="600px"
                        >
                          <Box p={3} pb={2}>
                            <Text fontSize="xs" fontWeight="700" color="gray.500" letterSpacing="wide" textTransform="uppercase">
                              Categories
                            </Text>
                          </Box>
                          <TabList px={2} pb={2}>
                            {state.categoryList.map((category, index) => (
                              <Tab
                                key={index}
                                justifyContent="flex-start"
                                px={3}
                                py={1}
                                mb={0.5}
                                borderRadius="md"
                                fontSize="13px"
                                fontWeight="400"
                                color="gray.700"
                                transition="all 0.2s"
                                border="none"
                                outline="none"
                                bg="transparent"
                                h="32px"
                                onMouseEnter={() => setSelectedCategory(index)}
                                _hover={{
                                  bgGradient: GRADIENTS.primary,
                                  color: 'white',
                                  fontWeight: '500'
                                }}
                                _selected={{
                                  bgGradient: GRADIENTS.primary,
                                  color: 'white',
                                  fontWeight: '600'
                                }}
                                _focus={{
                                  boxShadow: 'none',
                                  border: 'none',
                                  outline: 'none'
                                }}
                              >
                                <Text flex={1} textAlign="left">
                                  {category?.name || 'Category'}
                                </Text>
                              </Tab>
                            ))}
                          </TabList>
                        </Box>

                        {/* Right Content - Products Grid */}
                        <Box flex={1} overflowY="auto" maxH="450px">
                          <TabPanels>
                            {state.categoryList.map((category, catIndex) => {
                              const products = category?.product || [];
                              return (
                                <TabPanel key={catIndex} p={5}>
                                  <Flex justify="space-between" align="center" mb={4}>
                                    <Box>
                                      <Text fontSize="lg" fontWeight="700" color="gray.900" mb={1}>
                                        {category?.name}
                                      </Text>
                                      <Text fontSize="sm" color="gray.500">
                                        {products.length} products
                                      </Text>
                                    </Box>
                                    <Button
                                      as={Link}
                                      to={`/category/${category?.seo_name || category?.id}`}
                                      size="sm"
                                      variant="ghost"
                                      fontWeight="600"
                                      border="none"
                                      color="gray.700"
                                      textDecoration="none"
                                      _hover={{
                                        bg: 'transparent',
                                        bgGradient: GRADIENTS.primary,
                                        bgClip: 'text',
                                        textDecoration: 'none'
                                      }}
                                    >
                                      View All →
                                    </Button>
                                  </Flex>

                                  {products.length > 0 ? (
                                    <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                                      {products.map((product, prodIndex) => (
                                        <Link
                                          key={prodIndex}
                                          to={`/product/${category?.seo_name || category?.id}/${product?.seo_name || product?.id || ''}`}
                                          style={{ textDecoration: 'none' }}
                                        >
                                          <Box
                                            p={1.5}
                                            borderRadius="md"
                                            _hover={{
                                              bg: 'gray.50'
                                            }}
                                            transition="all 0.2s"
                                            cursor="pointer"
                                            minH="28px"
                                            display="flex"
                                            alignItems="center"
                                          >
                                            <Text
                                              fontSize="12px"
                                              fontWeight="500"
                                              color="gray.700"
                                              _hover={{ color: 'gray.900', fontWeight: '500' }}
                                              noOfLines={1}
                                              lineHeight="1.3"
                                            >
                                              {product?.name || 'Product'}
                                            </Text>
                                          </Box>
                                        </Link>
                                      ))}
                                    </Grid>
                                  ) : (
                                    <Box textAlign="center" py={12}>
                                      <Text color="gray.500" fontSize="sm">
                                        No products available in this category
                                      </Text>
                                    </Box>
                                  )}
                                </TabPanel>
                              );
                            })}
                          </TabPanels>
                        </Box>
                      </Flex>
                    </Tabs>
                  </PopoverBody>
                </PopoverContent>
              </Popover>

              {/* Pricing */}
              <Button
                as={Link}
                to="/pricecalculator"
                variant="ghost"
                size="md"
                fontWeight="600"
                fontSize="14px"
                color="gray.800"
                textDecoration="none"
                _hover={{
                  bg: 'transparent',
                  bgGradient: GRADIENTS.primary,
                  bgClip: 'text',
                  textDecoration: 'none'
                }}
                _active={{
                  bg: 'transparent'
                }}
                _focus={{
                  boxShadow: 'none'
                }}
                px={3}
              >
                Pricing
              </Button>

              {/* Track Order */}
              <Button
                as={Link}
                to="/track-order"
                variant="ghost"
                size="md"
                fontWeight="600"
                fontSize="14px"
                color="gray.800"
                textDecoration="none"
                _hover={{
                  bg: 'transparent',
                  bgGradient: GRADIENTS.primary,
                  bgClip: 'text',
                  textDecoration: 'none'
                }}
                _active={{
                  bg: 'transparent'
                }}
                _focus={{
                  boxShadow: 'none'
                }}
                px={3}
              >
                Track Order
              </Button>

              {/* More Menu */}
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  variant="ghost"
                  size="md"
                  fontWeight="600"
                  fontSize="14px"
                  color="gray.800"
                  bg="transparent"
                  border="none"
                  outline="none"
                  textDecoration="none"
                  _hover={{
                    bg: 'transparent',
                    bgGradient: GRADIENTS.primary,
                    bgClip: 'text',
                    textDecoration: 'none'
                  }}
                  _active={{
                    bg: 'transparent'
                  }}
                  _focus={{
                    boxShadow: 'none',
                    border: 'none',
                    outline: 'none',
                    bg: 'transparent'
                  }}
                  _expanded={{
                    bg: 'transparent',
                    color: 'gray.800'
                  }}
                  px={3}
                >
                  More
                </MenuButton>
                <MenuList
                  boxShadow="lg"
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  py={2}
                  minW="200px"
                >
                  <MenuItem
                    as={Link}
                    to="/information/about-printster"
                    fontSize="14px"
                    fontWeight="500"
                    py={2.5}
                    _hover={{
                      bgGradient: GRADIENTS.primary,
                      color: 'white'
                    }}
                  >
                    About Us
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    to="/faq/faq"
                    fontSize="14px"
                    fontWeight="500"
                    py={2.5}
                    _hover={{
                      bgGradient: GRADIENTS.primary,
                      color: 'white'
                    }}
                  >
                    FAQs
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    to="/information/hows-it-works"
                    fontSize="14px"
                    fontWeight="500"
                    py={2.5}
                    _hover={{
                      bgGradient: GRADIENTS.primary,
                      color: 'white'
                    }}
                  >
                    How It Works
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    to="/contact"
                    fontSize="14px"
                    fontWeight="500"
                    py={2.5}
                    _hover={{
                      bgGradient: GRADIENTS.primary,
                      color: 'white'
                    }}
                  >
                    Contact Us
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    to="/offers"
                    fontSize="14px"
                    fontWeight="500"
                    py={2.5}
                    _hover={{
                      bgGradient: GRADIENTS.primary,
                      color: 'white'
                    }}
                  >
                    Offers
                  </MenuItem>
                </MenuList>
              </Menu>
              <Button
                as={Link}
                to="/cart"
                bgGradient="linear(to-r, blue.400, purple.500, pink.500)"
                color="white"
                size="sm"
                fontWeight="700"
                fontSize="14px"
                border="none"
                textDecoration="none"
                _hover={{
                  bgGradient: GRADIENTS.primaryHover,
                  textDecoration: 'none'
                }}
                _active={{
                  bgGradient: GRADIENTS.primaryActive
                }}
                _focus={{
                  boxShadow: 'none'
                }}
                px={5}
                borderRadius="md"
              >
                Print Now
              </Button>
            </HStack>
          )}

          {/* Right Side Items */}
          {!isMobile && (
            <HStack spacing={4} alignItems="center">
              {/* Cart */}
              <Menu>
                <Tooltip label="Shopping Cart" placement="bottom" hasArrow>
                  <MenuButton
                    as={IconButton}
                    aria-label="Shopping Cart"
                    icon={
                      <Box position="relative">
                        <FaShoppingCart size="20px" />
                        {state.cartlength > 0 && (
                          <Badge
                            position="absolute"
                            top="-8px"
                            right="-10px"
                            bg="#4F47E5"
                            color="white"
                            borderRadius="full"
                            fontSize="11px"
                            fontWeight="700"
                            minW="20px"
                            h="20px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {state.cartlength}
                          </Badge>
                        )}
                      </Box>
                    }
                    variant="ghost"
                    size="md"
                    color="gray.600"
                    bg="transparent"
                    border="none"
                    boxShadow="none"
                    _hover={{
                      color: 'gray.900'
                    }}
                    _active={{
                      bg: 'transparent'
                    }}
                    _focus={{
                      boxShadow: 'none',
                      outline: 'none'
                    }}
                  />
                </Tooltip>
                {state.isLogin && (
                  <MenuList
                    maxW="380px"
                    boxShadow="xl"
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="xl"
                    p={0}
                    overflow="hidden"
                  >
                    {state.cartItem.length > 0 ? (
                      <Box>
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                          p={4}
                          bg="gray.50"
                          borderBottom="1px"
                          borderColor="gray.200"
                        >
                          <Text fontWeight="700" fontSize="md" color="gray.900">
                            Cart Items ({state.cartlength})
                          </Text>
                          <Button
                            as={Link}
                            to="/cart"
                            size="sm"
                            bg="#4F47E5"
                            color="white"
                            _hover={{ bg: '#3e38d4' }}
                            fontWeight="600"
                            border="none"
                          >
                            View All
                          </Button>
                        </Flex>
                        <VStack
                          spacing={0}
                          align="stretch"
                          maxH="300px"
                          overflowY="auto"
                          divider={<Divider />}
                        >
                          {state.cartItem.map((item, index) => {
                            const total = parseFloat(item?.printingChagres || item?.total || 0);
                            const copies = parseInt(item?.copies || 1);
                            const perItemPrice = total / copies;

                            return (
                              <Box key={index} p={4} _hover={{ bg: 'gray.50' }} transition="all 0.2s">
                                <Text fontSize="14px" fontWeight="600" mb={2} color="gray.900">
                                  {item?.name || item?.filename || 'Item'}
                                </Text>
                                <Flex justify="space-between" align="center">
                                  <Text fontSize="xs" color="gray.600">
                                    ₹{perItemPrice.toFixed(2)} × {copies}
                                  </Text>
                                  <Text fontSize="sm" fontWeight="700" color="gray.900">
                                    ₹{total.toFixed(2)}
                                  </Text>
                                </Flex>
                              </Box>
                            );
                          })}
                        </VStack>
                        <Box p={4} bg="gray.50" borderTop="1px" borderColor="gray.200">
                          <Flex justifyContent="space-between" alignItems="center">
                            <Text fontWeight="700" fontSize="md" color="gray.700">
                              Total:
                            </Text>
                            <Text fontWeight="800" fontSize="xl" color="gray.900">
                              ₹{cartTotal.toFixed(2)}
                            </Text>
                          </Flex>
                        </Box>
                      </Box>
                    ) : (
                      <Box p={8} textAlign="center">
                        <FaShoppingCart size="40px" color="#E2E8F0" style={{ margin: '0 auto 12px' }} />
                        <Text fontSize="sm" fontWeight="600" color="gray.900" mb={1}>
                          Your cart is empty
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Add items to get started
                        </Text>
                      </Box>
                    )}
                  </MenuList>
                )}
              </Menu>

              {/* User Menu or Login */}
              {state.isLogin ? (
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    variant="ghost"
                    size="md"
                    fontWeight="500"
                    fontSize="15px"
                    color="gray.700"
                    bg="transparent"
                    border="none"
                    boxShadow="none"
                    _hover={{ 
                      color: 'gray.900'
                    }}
                    _active={{ 
                      bg: 'transparent'
                    }}
                    _focus={{
                      boxShadow: 'none',
                      outline: 'none'
                    }}
                    px={3}
                    leftIcon={<Avatar size="xs" name={state.name} bg="gray.400" />}
                  >
                    {state.name || 'Account'}
                  </MenuButton>
                  <MenuList
                    boxShadow="xl"
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="xl"
                    overflow="hidden"
                    minW="240px"
                  >
                    <Box px={4} py={3} bg="gray.50" borderBottom="1px" borderColor="gray.200">
                      <HStack spacing={3}>
                        <FaWallet color="#48BB78" />
                        <Box>
                          <Text fontSize="xs" color="gray.500" mb={0.5}>
                            Print Cash Balance
                          </Text>
                          <Text fontSize="md" fontWeight="700" color="green.600">
                            ₹{parseFloat(state.printCash).toFixed(2)}
                          </Text>
                        </Box>
                      </HStack>
                    </Box>
                    <MenuItem
                      as={Link}
                      to="/profile"
                      fontSize="sm"
                      fontWeight="500"
                      py={3}
                      icon={<FaUser />}
                    >
                      User Profile
                    </MenuItem>
                    <MenuItem
                      as={Link}
                      to="/orders"
                      fontSize="sm"
                      fontWeight="500"
                      py={3}
                      icon={<FaShoppingBag />}
                    >
                      My Orders
                    </MenuItem>
                    <MenuItem
                      as={Link}
                      to="/change-password"
                      fontSize="sm"
                      fontWeight="500"
                      py={3}
                      icon={<FaBox />}
                    >
                      Change Password
                    </MenuItem>
                    <MenuDivider m={0} />
                    <MenuItem
                      onClick={logout}
                      fontSize="sm"
                      fontWeight="500"
                      color="red.500"
                      py={3}
                      icon={<FaSignOutAlt />}
                      _hover={{ bg: 'red.50' }}
                    >
                      Logout
                    </MenuItem>
                  </MenuList>
                </Menu>
              ) : (
                <Button
                  as={Link}
                  to="/login"
                  bgGradient="linear(to-r, blue.400, purple.500, pink.500)"
                  color="white"
                  size="sm"
                  fontWeight="700"
                  fontSize="14px"
                  border="none"
                  textDecoration="none"
                  _hover={{
                    bgGradient: GRADIENTS.primaryHover,
                    textDecoration: 'none'
                  }}
                  _active={{
                    bgGradient: GRADIENTS.primaryActive
                  }}
                  _focus={{
                    boxShadow: 'none'
                  }}
                  px={5}
                  
                  borderRadius="md"
                >
                  Sign In / Sign Up
                </Button>
              )}
            </HStack>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <HStack spacing={2}>
              <IconButton
                icon={<FaShoppingCart />}
                variant="ghost"
                size="md"
                position="relative"
                aria-label="Cart"
                border="none"
                as={Link}
                to="/cart"
                _focus={{ boxShadow: 'none' }}
              >
                {state.cartlength > 0 && (
                  <Badge
                    position="absolute"
                    top="5px"
                    right="5px"
                    bgGradient="linear(to-r, blue.400, purple.500, pink.500)"
                    color="white"
                    borderRadius="full"
                    fontSize="10px"
                    fontWeight="700"
                  >
                    {state.cartlength}
                  </Badge>
                )}
              </IconButton>
              <IconButton
                icon={<HamburgerIcon />}
                variant="ghost"
                onClick={onMobileMenuOpen}
                aria-label="Menu"
                border="none"
                _focus={{ boxShadow: 'none' }}
              />
            </HStack>
          )}
        </Flex>
      </Box>

      {/* Custom Mobile Menu - No Overlay Dependency */}
      {/* ROLLBACK: To use old Drawer, comment out the custom menu below and uncomment the Drawer section at the end of this file */}
      <Box
        position="fixed"
        top="0"
        right="0"
        bottom="0"
        width="320px"
        bg="white"
        zIndex={9999}
        boxShadow="2xl"
        transform={isMobileMenuOpen ? 'translateX(0)' : 'translateX(100%)'}
        transition="transform 0.3s ease-in-out"
        overflowY="auto"
        display={{ base: 'block', lg: 'none' }}
      >
        {/* Close Button */}
        <IconButton
          icon={<CloseIcon />}
          position="absolute"
          top="12px"
          right="12px"
          size="sm"
          variant="ghost"
          onClick={onMobileMenuClose}
          aria-label="Close menu"
          zIndex={1}
        />

        {/* Menu Header */}
        <Box p={4} borderBottom="1px" borderColor="gray.200">
          <Text fontSize="lg" fontWeight="700">Menu</Text>
        </Box>

        {/* Menu Content */}
        <Box pb={4}>
          <VStack spacing={0} align="stretch">
              {/* Mobile Search */}
              <Box p={4} borderBottom="1px" borderColor="gray.200">
                <form onSubmit={submitForm}>
                  <InputGroup size="md">
                    <Input
                      placeholder="Search for products..."
                      value={state.SearchValue}
                      onChange={updateForm}
                      bg="gray.50"
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="lg"
                      fontSize="14px"
                      _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                    />
                    <InputRightElement>
                      <IconButton
                        type="submit"
                        icon={<SearchIcon />}
                        size="sm"
                        bg="transparent"
                        color="gray.500"
                        aria-label="Search"
                        border="none"
                      />
                    </InputRightElement>
                  </InputGroup>
                </form>
              </Box>

              {/* User Profile Section */}
              {state.isLogin && (
                <Box p={4} bgGradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" color="white">
                  <HStack spacing={3} mb={3}>
                    <Avatar size="md" name={state.name} bg="white" color="#667eea" />
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="md" fontWeight="700">
                        {state.name}
                      </Text>
                      <HStack spacing={1}>
                        <Icon as={FaWallet} boxSize={3} />
                        <Text fontSize="sm" fontWeight="600">
                          ₹{parseFloat(state.printCash).toFixed(2)}
                        </Text>
                      </HStack>
                    </VStack>
                  </HStack>
                </Box>
              )}

              {/* Print Now Button */}
              <Box px={4} pt={4} pb={2}>
                <Button
                  as={Link}
                  to="/cart"
                  bg="#4F47E5"
                  color="white"
                  w="100%"
                  h="48px"
                  fontWeight="700"
                  fontSize="15px"
                  borderRadius="xl"
                  leftIcon={<FaPrint />}
                  _hover={{
                    bg: '#3e38d4',
                    transform: 'translateY(-2px)',
                    shadow: 'xl'
                  }}
                  _active={{
                    bg: '#2f2ab0',
                    transform: 'translateY(0)'
                  }}
                  shadow="lg"
                  onClick={onMobileMenuClose}
                >
                  Print Now
                </Button>
              </Box>

              {/* Quick Action Cards */}
              {state.isLogin && (
                <Box px={4} py={3}>
                  <SimpleGrid columns={3} spacing={2}>
                    <Button
                      as={Link}
                      to="/orders"
                      onClick={onMobileMenuClose}
                      h="70px"
                      bg="white"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="lg"
                      shadow="sm"
                      flexDirection="column"
                      _hover={{ bg: 'gray.50', borderColor: '#4F47E5', shadow: 'md' }}
                    >
                      <Icon as={FaShoppingBag} boxSize={5} color="#4F47E5" mb={1} />
                      <Text fontSize="xs" fontWeight="600" color="gray.700">Orders</Text>
                    </Button>

                    <Button
                      as={Link}
                      to="/profile"
                      onClick={onMobileMenuClose}
                      h="70px"
                      bg="white"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="lg"
                      shadow="sm"
                      flexDirection="column"
                      _hover={{ bg: 'gray.50', borderColor: '#4F47E5', shadow: 'md' }}
                    >
                      <Icon as={FaUser} boxSize={5} color="#4F47E5" mb={1} />
                      <Text fontSize="xs" fontWeight="600" color="gray.700">Profile</Text>
                    </Button>

                    <Button
                      as={Link}
                      to="/cart"
                      onClick={onMobileMenuClose}
                      h="70px"
                      bg="white"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="lg"
                      shadow="sm"
                      flexDirection="column"
                      position="relative"
                      _hover={{ bg: 'gray.50', borderColor: '#4F47E5', shadow: 'md' }}
                    >
                      <Icon as={FaShoppingCart} boxSize={5} color="#4F47E5" mb={1} />
                      <Text fontSize="xs" fontWeight="600" color="gray.700">Cart</Text>
                      {state.cartlength > 0 && (
                        <Badge
                          position="absolute"
                          top="8px"
                          right="8px"
                          bg="#4F47E5"
                          color="white"
                          borderRadius="full"
                          fontSize="10px"
                          minW="18px"
                          h="18px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {state.cartlength}
                        </Badge>
                      )}
                    </Button>
                  </SimpleGrid>
                </Box>
              )}

              {/* Main Menu Items */}
              <VStack spacing={0} align="stretch" px={4} py={2}>
                <Button
                  as={Link}
                  to="/"
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={onMobileMenuClose}
                  h="40px"
                  fontWeight="500"
                  fontSize="14px"
                  leftIcon={<Icon as={FaHome} />}
                  _hover={{ bg: 'gray.100' }}
                >
                  Home
                </Button>

                <Button
                  as={Link}
                  to="/product-all"
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={onMobileMenuClose}
                  h="40px"
                  fontWeight="500"
                  fontSize="14px"
                  leftIcon={<Icon as={FaBox} />}
                  _hover={{ bg: 'gray.100' }}
                >
                  All Products
                </Button>

                <Button
                  as={Link}
                  to="/pricecalculator"
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={onMobileMenuClose}
                  h="40px"
                  fontWeight="500"
                  fontSize="14px"
                  leftIcon={<Icon as={FaCalculator} />}
                  _hover={{ bg: 'gray.100' }}
                >
                  Price Calculator
                </Button>

                <Button
                  as={Link}
                  to="/track-order"
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={onMobileMenuClose}
                  h="40px"
                  fontWeight="500"
                  fontSize="14px"
                  leftIcon={<Icon as={FaTruck} />}
                  _hover={{ bg: 'gray.100' }}
                >
                  Track Order
                </Button>

                <Button
                  as={Link}
                  to="/offers"
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={onMobileMenuClose}
                  h="40px"
                  fontWeight="500"
                  fontSize="14px"
                  leftIcon={<Icon as={FaTag} />}
                  _hover={{ bg: 'gray.100' }}
                >
                  Offers
                </Button>
              </VStack>

              <Divider />

              {/* Your Information Section */}
              {state.isLogin && (
                <>
                  <Box px={4} pt={3} pb={2}>
                    <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                      Your Information
                    </Text>
                  </Box>

                  <VStack spacing={0} align="stretch" px={4}>
                    <Button
                      as={Link}
                      to="/profile"
                      variant="ghost"
                      justifyContent="flex-start"
                      onClick={onMobileMenuClose}
                      h="40px"
                      fontWeight="500"
                      fontSize="14px"
                      leftIcon={<FaUser />}
                      _hover={{ bg: 'gray.100' }}
                    >
                      Personal Information
                    </Button>

                    <Button
                      as={Link}
                      to="/change-password"
                      variant="ghost"
                      justifyContent="flex-start"
                      onClick={onMobileMenuClose}
                      h="40px"
                      fontWeight="500"
                      fontSize="14px"
                      leftIcon={<Icon as={FaLock} />}
                      _hover={{ bg: 'gray.100' }}
                    >
                      Change Password
                    </Button>

                    <Button
                      as={Link}
                      to="/address"
                      variant="ghost"
                      justifyContent="flex-start"
                      onClick={onMobileMenuClose}
                      h="40px"
                      fontWeight="500"
                      fontSize="14px"
                      leftIcon={<Icon as={FaMapMarkerAlt} />}
                      _hover={{ bg: 'gray.100' }}
                    >
                      Saved Addresses
                    </Button>

                    <Button
                      as={Link}
                      to="/print-cash-history"
                      variant="ghost"
                      justifyContent="flex-start"
                      onClick={onMobileMenuClose}
                      h="40px"
                      fontWeight="500"
                      fontSize="14px"
                      leftIcon={<Icon as={FaWallet} />}
                      _hover={{ bg: 'gray.100' }}
                    >
                      Print Cash
                    </Button>
                  </VStack>

                  <Divider mt={2} />
                </>
              )}

              {/* Other Information Section */}
              <Box px={4} pt={3} pb={2}>
                <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                  Other Information
                </Text>
              </Box>

              <VStack spacing={0} align="stretch" px={4}>
                <Button
                  as={Link}
                  to="/information/about-printster"
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={onMobileMenuClose}
                  h="40px"
                  fontWeight="500"
                  fontSize="14px"
                  leftIcon={<Icon as={FaInfoCircle} />}
                  _hover={{ bg: 'gray.100' }}
                >
                  About Us
                </Button>

                <Button
                  as={Link}
                  to="/faq/faq"
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={onMobileMenuClose}
                  h="40px"
                  fontWeight="500"
                  fontSize="14px"
                  leftIcon={<Icon as={FaQuestionCircle} />}
                  _hover={{ bg: 'gray.100' }}
                >
                  FAQs
                </Button>

                <Button
                  as={Link}
                  to="/information/hows-it-works"
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={onMobileMenuClose}
                  h="40px"
                  fontWeight="500"
                  fontSize="14px"
                  leftIcon={<Icon as={FaCog} />}
                  _hover={{ bg: 'gray.100' }}
                >
                  How It Works
                </Button>

                <Button
                  as={Link}
                  to="/contact"
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={onMobileMenuClose}
                  h="40px"
                  fontWeight="500"
                  fontSize="14px"
                  leftIcon={<Icon as={FaPhone} />}
                  _hover={{ bg: 'gray.100' }}
                >
                  Contact Us
                </Button>
              </VStack>

              {/* Logout or Sign In Button */}
              {state.isLogin ? (
                <Box px={4} pt={4} pb={3}>
                  <Button
                    onClick={logout}
                    bg="red.500"
                    color="white"
                    w="100%"
                    h="44px"
                    fontWeight="600"
                    fontSize="14px"
                    borderRadius="lg"
                    leftIcon={<FaSignOutAlt />}
                    _hover={{
                      bg: 'red.600',
                      transform: 'translateY(-1px)',
                      shadow: 'md'
                    }}
                    _active={{
                      bg: 'red.700',
                      transform: 'translateY(0)'
                    }}
                  >
                    Logout
                  </Button>
                </Box>
              ) : (
                <Box px={4} pt={4} pb={3}>
                  <Button
                    as={Link}
                    to="/login"
                    bg="#4F47E5"
                    color="white"
                    w="100%"
                    h="44px"
                    fontWeight="600"
                    fontSize="14px"
                    borderRadius="lg"
                    _hover={{
                      bg: '#3e38d4',
                      transform: 'translateY(-1px)',
                      shadow: 'md'
                    }}
                    _active={{
                      bg: '#2f2ab0',
                      transform: 'translateY(0)'
                    }}
                    onClick={onMobileMenuClose}
                  >
                    Sign In / Sign Up
                  </Button>
                </Box>
              )}
            </VStack>
        </Box>
      </Box>

      {/* Backdrop/Overlay for Custom Menu */}
      {isMobileMenuOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blackAlpha.600"
          zIndex={9998}
          onClick={onMobileMenuClose}
          display={{ base: 'block', lg: 'none' }}
        />
      )}

      {/* ROLLBACK CODE - OLD DRAWER IMPLEMENTATION */}
      {/* Uncomment this section and comment out the custom menu above to rollback */}
      {/*
      <Drawer
        isOpen={isMobileMenuOpen}
        placement="right"
        onClose={onMobileMenuClose}
        size="sm"
        closeOnOverlayClick={true}
        closeOnEsc={true}
        returnFocusOnClose={false}
        blockScrollOnMount={false}
        trapFocus={false}
        lockFocusAcrossFrames={false}
        preserveScrollBarGap={false}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottom="1px" borderColor="gray.200">
            <Text fontSize="lg" fontWeight="700">Menu</Text>
          </DrawerHeader>
          <DrawerBody p={0}>
            <VStack spacing={0} align="stretch">
              [... all menu content ...]
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      */}
    </Box>
  );
});

Header.displayName = 'Header';

export default Header;